/**
 * Export Utilities for Admin Panel
 *
 * スキルチェックデータのエクスポート機能を提供
 * - CSV エクスポート
 * - JSON エクスポート
 * - 詳細データエクスポート（LLM入出力含む）
 */

import type {
  ScoringBreakdown,
  SkillCheckAdminItem,
  SkillCheckJobDetail,
  SkillCheckJobStepDetail,
} from "../types/admin.types";

// ========================================
// 型定義
// ========================================

/**
 * 詳細エクスポートデータ
 */
export type DetailedExportData = {
  /** エクスポート日時 */
  exportedAt: string;
  /** ジョブ詳細 */
  job: SkillCheckJobDetail;
  /** LLMステップ詳細 */
  steps: SkillCheckJobStepDetail[];
  /** スコアリング内訳（完了時のみ） */
  scoring: ScoringBreakdown | null;
};

/**
 * 一覧エクスポートメタデータ
 */
type ListExportMetadata = {
  /** エクスポート日時 */
  exportedAt: string;
  /** 総件数 */
  totalCount: number;
  /** フィルタ条件 */
  filters: Record<string, unknown>;
};

/**
 * JSON一覧エクスポートデータ
 */
export type ListExportData = {
  /** メタデータ */
  metadata: ListExportMetadata;
  /** スキルチェック一覧 */
  items: SkillCheckAdminItem[];
};

// ========================================
// CSV エクスポート
// ========================================

/**
 * CSVヘッダー定義
 */
const CSV_HEADERS = [
  "ジョブID",
  "ステータス",
  "候補者名",
  "候補者ソース",
  "求人タイトル",
  "チーム名",
  "会社名",
  "マッチレベル",
  "作成日時",
  "処理開始日時",
  "処理完了日時",
  "処理時間(秒)",
  "エラーコード",
  "エラーメッセージ",
  "リトライ回数",
];

/**
 * 値をCSV用にエスケープ
 * - ダブルクォートを含む場合は2重にする
 * - カンマ、改行、ダブルクォートを含む場合はダブルクォートで囲む
 */
function escapeCSVValue(value: string | number | null | undefined): string {
  if (value === null || value === undefined) {
    return "";
  }

  const stringValue = String(value);

  // ダブルクォート、カンマ、改行を含む場合はエスケープが必要
  if (stringValue.includes('"') || stringValue.includes(",") || stringValue.includes("\n")) {
    return `"${stringValue.replace(/"/g, '""')}"`;
  }

  return stringValue;
}

/**
 * 処理時間を計算（秒）
 */
function calculateProcessingTime(startedAt: string | null, completedAt: string | null): string {
  if (!(startedAt && completedAt)) {
    return "";
  }

  const start = new Date(startedAt).getTime();
  const end = new Date(completedAt).getTime();
  const durationMs = end - start;

  return (durationMs / 1000).toFixed(1);
}

/**
 * ISO8601日時を日本語形式に変換
 */
function formatDateTime(isoString: string | null): string {
  if (!isoString) {
    return "";
  }

  return new Date(isoString).toLocaleString("ja-JP", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
}

/**
 * スキルチェック一覧をCSV行に変換
 */
function itemToCSVRow(item: SkillCheckAdminItem): string {
  const values = [
    item.id,
    item.status,
    item.candidateName,
    item.candidateSource,
    item.jobPostingTitle,
    item.teamName,
    item.companyName,
    item.overallMatchLevel,
    formatDateTime(item.createdAt),
    formatDateTime(item.startedAt),
    formatDateTime(item.completedAt),
    calculateProcessingTime(item.startedAt, item.completedAt),
    item.errorCode,
    item.errorMessage,
    item.retryCount,
  ];

  return values.map(escapeCSVValue).join(",");
}

/**
 * スキルチェック一覧をCSV形式でエクスポート
 *
 * @param items - スキルチェック一覧
 * @returns CSV文字列
 */
export function exportToCSV(items: SkillCheckAdminItem[]): string {
  const headerRow = CSV_HEADERS.join(",");
  const dataRows = items.map(itemToCSVRow);

  // BOM付きUTF-8でExcelでも日本語が正しく表示されるようにする
  return `\uFEFF${headerRow}\n${dataRows.join("\n")}`;
}

/**
 * CSVファイルをダウンロード
 *
 * @param items - スキルチェック一覧
 * @param filename - ファイル名（拡張子なし）
 */
export function downloadCSV(items: SkillCheckAdminItem[], filename?: string): void {
  const csv = exportToCSV(items);
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });

  const defaultFilename = `skill-checks-${new Date().toISOString().slice(0, 10)}`;
  downloadBlob(blob, `${filename ?? defaultFilename}.csv`);
}

// ========================================
// JSON エクスポート
// ========================================

/**
 * スキルチェック一覧をJSON形式でエクスポート
 *
 * @param items - スキルチェック一覧
 * @param filters - 適用されたフィルタ条件
 * @returns JSON文字列
 */
export function exportToJSON(
  items: SkillCheckAdminItem[],
  filters: Record<string, unknown> = {},
): string {
  const exportData: ListExportData = {
    metadata: {
      exportedAt: new Date().toISOString(),
      totalCount: items.length,
      filters,
    },
    items,
  };

  return JSON.stringify(exportData, null, 2);
}

/**
 * JSONファイルをダウンロード
 *
 * @param items - スキルチェック一覧
 * @param filters - 適用されたフィルタ条件
 * @param filename - ファイル名（拡張子なし）
 */
export function downloadJSON(
  items: SkillCheckAdminItem[],
  filters: Record<string, unknown> = {},
  filename?: string,
): void {
  const json = exportToJSON(items, filters);
  const blob = new Blob([json], { type: "application/json;charset=utf-8" });

  const defaultFilename = `skill-checks-${new Date().toISOString().slice(0, 10)}`;
  downloadBlob(blob, `${filename ?? defaultFilename}.json`);
}

// ========================================
// 詳細データエクスポート（LLM入出力含む）
// ========================================

/**
 * 詳細データをJSON形式でエクスポート
 *
 * @param job - ジョブ詳細
 * @param steps - LLMステップ詳細
 * @param scoring - スコアリング内訳（オプション）
 * @returns JSON文字列
 */
export function exportDetailedToJSON(
  job: SkillCheckJobDetail,
  steps: SkillCheckJobStepDetail[],
  scoring: ScoringBreakdown | null = null,
): string {
  const exportData: DetailedExportData = {
    exportedAt: new Date().toISOString(),
    job,
    steps,
    scoring,
  };

  return JSON.stringify(exportData, null, 2);
}

/**
 * 詳細データJSONファイルをダウンロード
 *
 * @param job - ジョブ詳細
 * @param steps - LLMステップ詳細
 * @param scoring - スコアリング内訳（オプション）
 * @param filename - ファイル名（拡張子なし）
 */
export function downloadDetailedJSON(
  job: SkillCheckJobDetail,
  steps: SkillCheckJobStepDetail[],
  scoring: ScoringBreakdown | null = null,
  filename?: string,
): void {
  const json = exportDetailedToJSON(job, steps, scoring);
  const blob = new Blob([json], { type: "application/json;charset=utf-8" });

  const defaultFilename = `skill-check-detail-${job.id}-${new Date().toISOString().slice(0, 10)}`;
  downloadBlob(blob, `${filename ?? defaultFilename}.json`);
}

// ========================================
// ヘルパー関数
// ========================================

/**
 * Blobをファイルとしてダウンロード
 *
 * @param blob - ダウンロードするBlob
 * @param filename - ファイル名
 */
function downloadBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

/**
 * ファイルサイズを人間が読みやすい形式に変換
 *
 * @param bytes - バイト数
 * @returns 人間が読みやすい形式の文字列
 */
export function formatFileSize(bytes: number): string {
  if (bytes === 0) {
    return "0 B";
  }

  const units = ["B", "KB", "MB", "GB"];
  const k = 1024;
  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return `${Number.parseFloat((bytes / k ** i).toFixed(1))} ${units[i]}`;
}

/**
 * エクスポートファイルサイズを推定
 * （大量データのエクスポート前に警告を表示するため）
 *
 * @param items - スキルチェック一覧
 * @param format - エクスポート形式
 * @returns 推定ファイルサイズ（バイト）
 */
export function estimateExportSize(items: SkillCheckAdminItem[], format: "csv" | "json"): number {
  // 1アイテムあたりの平均サイズ（概算）
  const avgSizePerItem = format === "csv" ? 500 : 800;
  const headerSize = format === "csv" ? 200 : 100;

  return headerSize + items.length * avgSizePerItem;
}
